import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useOrgProjectRouteContext } from "@/lib/org-project-context/hooks";
import { dismissOnboarding } from "./dismiss-onboarding";
import type { EligibilityResponse } from "./get-eligibility";
import {
  type OnboardingProgressQueryOptions,
  onboardingProgressQueryOptions,
} from "./get-onboarding-progress";
import { onboardingKeys } from "./query-keys";

export const useGetOnboardingProgress = (
  options?: OnboardingProgressQueryOptions
) => {
  const { organizationId, projectId } = useOrgProjectRouteContext();
  return useQuery(
    onboardingProgressQueryOptions(organizationId, projectId, options)
  );
};

/**
 * Dismisses onboarding for the current account. The dismissed state is
 * account-scoped. Optimistically flips eligibility to dismissed; the progress
 * query refetches to null on the subsequent invalidation.
 */
export const useDismissOnboarding = () => {
  const queryClient = useQueryClient();
  const { organizationId, projectId } = useOrgProjectRouteContext();
  const scope = { organizationId, projectId };
  const eligibilityKey = onboardingKeys.eligibility({ scope });

  return useMutation({
    mutationFn: dismissOnboarding,
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: onboardingKeys.all() });
      const previousEligibility =
        queryClient.getQueryData<EligibilityResponse>(eligibilityKey);
      if (previousEligibility != null) {
        queryClient.setQueryData<EligibilityResponse>(eligibilityKey, {
          ...previousEligibility,
          dismissed: true,
        });
      }
      return { previousEligibility };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousEligibility !== undefined) {
        queryClient.setQueryData(eligibilityKey, context.previousEligibility);
      }
    },
    meta: {
      invalidates: [onboardingKeys.all()],
      errorMessage: "Failed to dismiss onboarding",
    },
  });
};
